import { useState, useEffect, useCallback, useRef } from "react";
import type { StateMap } from "../types";
import type { HiddenMap } from "../lib/hiddenFixes";
import { AuditSection, removeCreateDraft } from "./AuditSection";
import { showAlert } from "../lib/dialog";
import { confirmDeletePopover } from "../lib/confirmPopover";

type ToolPage = { id: number; title: string; link: string; type: "page" | "post"; created?: string };

// The "Build create tasks" run is tracked at MODULE level so it survives the component unmounting
// when the user switches tabs mid-build: the built cards are written straight to localStorage (not
// via setState → useEffect, which no-ops after unmount), and the mount effect re-attaches to restore
// the spinner and apply the result. Prevents a lost build and a duplicate build on return.
let cdBuildInFlight: Promise<{ ok: boolean; count: number; error?: string }> | null = null;

interface Props {
  state: StateMap;
  siteBrand?: string;
  auditDate?: string;
  onApprove: (id: string, message?: string, isCreate?: boolean) => void;
  onIgnore: (id: string, checked: boolean) => void;
  onDone: (id: string, checked: boolean) => void;
  onReset: (id: string) => void;
  hidden: HiddenMap;
  onToggleHide: (id: string) => void;
  connected: boolean;
  fetchPageTags: (pathSlug: string) => Promise<string[] | null>;
  onOpenSchemaSetup?: () => void;
  listToolCreatedPages: () => Promise<ToolPage[]>;
  getClient?: () => import("../lib/wpClient").WPClient | null;
}

export function ContentDrawer(props: Props) {
  const { connected, listToolCreatedPages } = props;

  // ── Bulk Article Creation ───────────────────────────────────
  // Persisted to localStorage so the pasted titles and generated task cards survive
  // switching to another tab and back (the component unmounts on tab change).
  const [titles, setTitles] = useState(() => { try { return localStorage.getItem("lsg_cd_titles") ?? ""; } catch { return ""; } });
  const [cardsHtml, setCardsHtml] = useState(() => { try { return localStorage.getItem("lsg_cd_cards") ?? ""; } catch { return ""; } });
  const [building, setBuilding] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => { try { localStorage.setItem("lsg_cd_titles", titles); } catch { /* ignore */ } }, [titles]);
  useEffect(() => { try { localStorage.setItem("lsg_cd_cards", cardsHtml); } catch { /* ignore */ } }, [cardsHtml]);

  // ── Preserve in-progress card edits/generated content across tab switches ──
  // The cards' fields + WYSIWYG editor content live in plain DOM (rendered via
  // AuditSection's dangerouslySetInnerHTML), which is destroyed when this component
  // unmounts on tab change. Snapshot that live state to localStorage on unmount and
  // restore it after the cards re-render.
  const cardsRef = useRef<HTMLDivElement>(null);
  const SNAP_KEY = "lsg_cd_snapshot";
  // Monotonically increasing card-id counter. Persisted so a new card NEVER reuses a
  // deleted card's `cd-N` id — reuse would let the deleted card's saved snapshot bleed
  // into the fresh card's fields.
  const SEQ_KEY = "lsg_cd_seq";
  const FIELD_IDS = ["title", "mt", "sl", "par", "md", "pt", "pd", "aft", "kw", "wc", "type", "ci", "summaryheading"] as const;

  const snapshotCards = useCallback(() => {
    const root = cardsRef.current;
    if (!root) return;
    const snap: Record<string, Record<string, unknown>> = {};
    root.querySelectorAll<HTMLElement>('[id^="cft-content-"]').forEach(editor => {
      const fixId = editor.id.replace(/^cft-content-/, "");
      const fields: Record<string, string> = {};
      for (const f of FIELD_IDS) {
        const el = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#cft-${f}-${fixId}`);
        if (el) fields[f] = el.value;
      }
      // The summarization toggle is a checkbox — capture its checked state as a string.
      const summCb = root.querySelector<HTMLInputElement>(`#cft-summarize-${fixId}`);
      if (summCb) fields.summarize = summCb.checked ? "1" : "";
      const row = root.querySelector<HTMLElement>(`#cfr-${fixId}`);
      const wrap = root.querySelector<HTMLElement>(`#cfp-content-wrap-${fixId}`);
      const submit = root.querySelector<HTMLElement>(`.create-submit-btn[data-fix-id="${fixId}"]`);
      snap[fixId] = {
        fields,
        editor: editor.innerHTML,
        faq: editor.dataset.faqAnswer ?? "",
        // Whether the create FORM was expanded — this is the real "open" state. Derive it
        // from the form row, NOT from the content wrap: the wrap turns visible as soon as
        // content is generated, so using it would force-reopen every generated (but
        // collapsed) card whenever any card is deleted/re-rendered.
        open: row ? row.style.display !== "none" : false,
        // Whether generated content exists (wrap revealed). Restored so the content is in
        // place when the form is opened, but on its own it never expands the form.
        contentShown: wrap ? wrap.style.display !== "none" : false,
        publish: submit ? submit.style.display !== "none" : false,
      };
    });
    try { localStorage.setItem(SNAP_KEY, JSON.stringify(snap)); } catch { /* ignore */ }
  }, []);

  const restoreCards = useCallback(() => {
    const root = cardsRef.current;
    if (!root) return;
    let snap: Record<string, { fields?: Record<string, string>; editor?: string; faq?: string; open?: boolean; contentShown?: boolean; shown?: boolean; publish?: boolean }>;
    try { snap = JSON.parse(localStorage.getItem(SNAP_KEY) ?? "{}"); } catch { return; }
    for (const [fixId, s] of Object.entries(snap)) {
      for (const f of FIELD_IDS) {
        const v = s.fields?.[f];
        if (v == null) continue;
        const el = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`#cft-${f}-${fixId}`);
        if (el) el.value = v;
      }
      // Restore the summarization toggle + reveal its heading field to match.
      const summCb = root.querySelector<HTMLInputElement>(`#cft-summarize-${fixId}`);
      if (summCb) {
        summCb.checked = s.fields?.summarize === "1";
        const summWrap = root.querySelector<HTMLElement>(`#cft-summaryheading-wrap-${fixId}`);
        if (summWrap) summWrap.style.display = summCb.checked ? "block" : "none";
      }
      const editor = root.querySelector<HTMLElement>(`#cft-content-${fixId}`);
      if (editor && s.editor) {
        editor.innerHTML = s.editor;
        if (s.faq) editor.dataset.faqAnswer = s.faq;
        // Don't auto-regenerate over restored content when the card is reopened.
        const expand = root.querySelector<HTMLElement>(`.create-expand-btn[data-target="cfr-${fixId}"]`);
        if (expand) expand.dataset.autogenDone = "1";
        const gen = root.querySelector<HTMLElement>(`.generate-content-btn[data-fix-id="${fixId}"]`);
        if (gen) gen.textContent = "Regenerate Content";
      }
      // Reveal the generated-content wrap (so the content shows once the form is open).
      // `?? s.shown` keeps older snapshots (pre-split field) working.
      if (s.contentShown ?? s.shown) {
        const wrap = root.querySelector<HTMLElement>(`#cfp-content-wrap-${fixId}`);
        if (wrap) wrap.style.display = "";
      }
      // Only re-expand the form if it was actually open when snapshotted — never just
      // because the card has content.
      if (s.open) {
        const row = root.querySelector<HTMLElement>(`#cfr-${fixId}`);
        if (row) row.style.display = "";
      }
      if (s.publish) {
        const submit = root.querySelector<HTMLElement>(`.create-submit-btn[data-fix-id="${fixId}"]`);
        if (submit) submit.style.display = "";
      }
    }
  }, []);

  // Remove a single card (trash button). Snapshot the live state of the OTHER cards
  // first so their generated content survives the re-render, drop the trashed card from
  // both the snapshot and the markup, then re-render.
  const removeCard = useCallback((fixId: string) => {
    snapshotCards();
    try {
      const snap = JSON.parse(localStorage.getItem(SNAP_KEY) ?? "{}");
      delete snap[fixId];
      localStorage.setItem(SNAP_KEY, JSON.stringify(snap));
    } catch { /* ignore */ }
    removeCreateDraft(fixId); // deleting the card clears its saved draft (published cards keep theirs)
    setCardsHtml(prev => {
      const tmp = document.createElement("div");
      tmp.innerHTML = prev;
      tmp.querySelector(`#hdr-${fixId}`)?.remove();
      tmp.querySelector(`#cfr-${fixId}`)?.remove();
      return tmp.innerHTML.trim();
    });
  }, [snapshotCards]);

  // Snapshot on unmount (tab switch); restore after the cards render.
  useEffect(() => () => snapshotCards(), [snapshotCards]);
  useEffect(() => { if (cardsHtml) restoreCards(); }, [cardsHtml, restoreCards]);

  // Wire the per-card trash buttons (Content Drawer only).
  useEffect(() => {
    const root = cardsRef.current;
    if (!root) return;
    // No "Ignore" or "✓ Complete" checkbox actions in the Content Drawer — strip every .ac-chk
    // label (and its adjacent separator) from every card, including older cached cards.
    root.querySelectorAll<HTMLElement>(".um-new .ac label.ac-chk").forEach(lbl => {
      const prev = lbl.previousElementSibling;
      if (prev?.classList.contains("ac-sep")) prev.remove();
      else if (lbl.nextElementSibling?.classList.contains("ac-sep")) lbl.nextElementSibling.remove();
      lbl.remove();
    });
    const cleanups: Array<() => void> = [];
    root.querySelectorAll<HTMLElement>(".cd-trash-btn").forEach(btn => {
      const fixId = btn.getAttribute("data-fix");
      if (!fixId) return;
      const h = (e: Event) => { e.preventDefault(); confirmDeletePopover(btn, () => removeCard(fixId)); };
      btn.addEventListener("click", h);
      cleanups.push(() => btn.removeEventListener("click", h));
    });
    // Make the whole card header clickable to expand/collapse the create form (mirrors the
    // Express Meta Data cards). Clicks on the action controls / trash are left to their own
    // handlers so they keep working.
    root.querySelectorAll<HTMLElement>(".um.um-new .um-hd").forEach(hd => {
      const onClick = (e: Event) => {
        if ((e.target as HTMLElement).closest(".ac, .cd-trash-btn, button, input, label, a")) return;
        hd.querySelector<HTMLButtonElement>(".create-expand-btn")?.click();
      };
      hd.addEventListener("click", onClick);
      cleanups.push(() => hd.removeEventListener("click", onClick));
    });
    return () => cleanups.forEach(fn => fn());
  }, [cardsHtml, removeCard]);

  // Status line for a completed build (shared by the trigger and the re-attach effect).
  const buildStatus = (r: { ok: boolean; count: number; error?: string }) =>
    r.ok
      ? `${r.count} article task${r.count === 1 ? "" : "s"} added — click ⚡ Create on each to auto-generate optimized content and publish.`
      : `⚠ ${r.error ?? "Failed to build tasks"}`;

  async function handleContinue() {
    const list = titles.split("\n").map(t => t.trim()).filter(Boolean);
    if (!list.length) {
      void showAlert("Paste at least one article title (one per line), then click Continue.", { title: "No Titles" });
      return;
    }
    if (building || cdBuildInFlight) return; // a build is already running (possibly from before a tab switch)
    setBuilding(true);
    setStatus("Building create tasks…");
    setTitles(""); // clear the box so the same topics aren't added again on a second click
    // Run the whole build (fetch + persisting the new cards to localStorage) as a module-level
    // promise so it completes and saves even if this component unmounts on a tab switch. All
    // localStorage reads/writes here are unmount-safe; only the setState calls below need a live
    // component, and the mount effect re-applies them on return.
    cdBuildInFlight = (async () => {
      // Append new cards after the existing ones (unique cd-{n} ids). Read the current cards from
      // localStorage (not React state) so the index math is correct regardless of mount status.
      const existing = (() => { try { return localStorage.getItem("lsg_cd_cards") ?? ""; } catch { return ""; } })();
      const scannedMax = (() => {
        let max = -1;
        const re = /id="hdr-cd-(\d+)"/g;
        let m: RegExpExecArray | null;
        while ((m = re.exec(existing))) max = Math.max(max, parseInt(m[1], 10));
        return max;
      })();
      let storedSeq = 0;
      try { storedSeq = parseInt(localStorage.getItem(SEQ_KEY) ?? "0", 10) || 0; } catch { /* ignore */ }
      const nextIndex = Math.max(scannedMax + 1, storedSeq);
      // Clear any stale snapshot entries for the ids we're about to create so freshly-built
      // cards are prefilled only from their topic — never from a previously-created card.
      try {
        const snap = JSON.parse(localStorage.getItem(SNAP_KEY) ?? "{}");
        for (let i = 0; i < list.length; i++) delete snap[`cd-${nextIndex + i}`];
        localStorage.setItem(SNAP_KEY, JSON.stringify(snap));
      } catch { /* ignore */ }
      const res = await fetch("/api/build-create-cards", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ titles: list, startIndex: nextIndex }),
      });
      const data = await res.json() as { ok: boolean; html?: string; error?: string };
      if (data.ok && data.html) {
        try { localStorage.setItem(SEQ_KEY, String(nextIndex + list.length)); } catch { /* ignore */ }
        // Append straight to localStorage (re-read to fold in any concurrent card delete) so the
        // new cards persist even if the component already unmounted — never rely on setState here.
        try { const cur = localStorage.getItem("lsg_cd_cards") ?? ""; localStorage.setItem("lsg_cd_cards", cur + data.html); } catch { /* ignore */ }
        return { ok: true, count: list.length };
      }
      return { ok: false, count: list.length, error: data.error };
    })();
    void cdBuildInFlight.catch(() => {}).finally(() => { cdBuildInFlight = null; });
    try {
      const r = await cdBuildInFlight;
      try { setCardsHtml(localStorage.getItem("lsg_cd_cards") ?? ""); } catch { /* ignore */ }
      setStatus(buildStatus(r));
    } catch (e) {
      setStatus(`⚠ ${e instanceof Error ? e.message : "Network error"}`);
    } finally {
      setBuilding(false);
    }
  }

  // Re-attach to a "Build create tasks" run still in flight from before a tab switch: restore the
  // spinner and apply the built cards (already written to localStorage) when it finishes.
  useEffect(() => {
    if (!cdBuildInFlight) return;
    let alive = true;
    setBuilding(true);
    setStatus("Building create tasks…");
    cdBuildInFlight
      .then(r => { if (alive) { try { setCardsHtml(localStorage.getItem("lsg_cd_cards") ?? ""); } catch { /* ignore */ } setStatus(buildStatus(r)); } })
      .catch(e => { if (alive) setStatus(`⚠ ${e instanceof Error ? e.message : "Network error"}`); })
      .finally(() => { if (alive) setBuilding(false); });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── History ─────────────────────────────────────────────────
  const [history, setHistory] = useState<ToolPage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!connected) return;
    setHistoryLoading(true);
    try {
      const pages = await listToolCreatedPages();
      pages.sort((a, b) => (b.created ?? "").localeCompare(a.created ?? ""));
      setHistory(pages);
      setHistoryLoaded(true);
    } catch { /* ignore — leave previous list */ }
    finally { setHistoryLoading(false); }
  }, [connected, listToolCreatedPages]);

  useEffect(() => { if (connected) void loadHistory(); }, [connected, loadHistory]);

  const fmtDate = (iso?: string) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? iso.slice(0, 10)
      : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  return (
    <div className="content-drawer">
      <div className="sh"><h2>Content Drawer</h2><p>Bulk-create articles &amp; track everything the tool has published</p></div>

      {/* ── Section 1: Bulk Article Creation ── */}
      <div className="cd-section">
        <div className="cd-section-hd">
          <span className="cd-hd-brand">Bulk Page Creation</span>
          <span className="audit-tip" style={{ marginLeft: 2 }}>
            <span className="audit-tip-icon">i</span>
            <span className="audit-tip-box" style={{ textTransform: "none", fontFamily: "'Inter','Mulish',sans-serif", fontWeight: 400, letterSpacing: "normal" }}>
              {"The page title will be searched live in Google and the top 5 organic pages ranking for that title will be analyzed to determine average wordcount and keyword density amongst the 5 competitor pages. The content generated will have a higher keyword density, wordcount, LSI keyword variations, NLP entities, integrated internal links, and the page schema will be added upon publishing to WordPress."}
            </span>
          </span>
        </div>
        <p className="cd-help">
          Paste one Article Page Title per line, then click <strong>Continue</strong>. Each title becomes its own
          create task below — click <strong>⚡ Create</strong> on any task to auto-generate optimized content and
          publish it to WordPress.
        </p>
        <textarea
          className="cd-titles"
          value={titles}
          onChange={e => setTitles(e.target.value)}
          rows={6}
          placeholder={"Enter one article title per line"}
        />
        <div className="cd-actions">
          <button className="cd-continue-btn" onClick={handleContinue} disabled={building}>
            {building ? "Building…" : "Continue →"}
          </button>
          {status && <span className="cd-status">{status}</span>}
        </div>
      </div>

      {/* Generated create-task cards — rendered through AuditSection so they get the full
          create-form wiring (auto-generate → WYSIWYG editor → Publish to WordPress). */}
      {cardsHtml && (
        <div className="cd-cards" ref={cardsRef}>
          <AuditSection
            id="contentdrawer"
            html={cardsHtml}
            state={props.state}
            siteBrand={props.siteBrand}
            auditDate={props.auditDate}
            onApprove={props.onApprove}
            onIgnore={props.onIgnore}
            onDone={props.onDone}
            onReset={props.onReset}
            hidden={props.hidden}
            onToggleHide={props.onToggleHide}
            connected={props.connected}
            fetchPageTags={props.fetchPageTags}
            onOpenSchemaSetup={props.onOpenSchemaSetup}
            getClient={props.getClient}
          />
        </div>
      )}

      {/* ── Section 2: History ── */}
      <div className="cd-section cd-history-section">
        <div className="cd-section-hd">
          <span className="cd-hd-brand">History - Published Pages</span>
          <span className="audit-tip" style={{ marginLeft: 2 }}>
            <span className="audit-tip-icon">i</span>
            <span className="audit-tip-box" style={{ textTransform: "none", fontFamily: "'Inter','Mulish',sans-serif", fontWeight: 400, letterSpacing: "normal" }}>
              {"The History section shows historical information for 'new' pages added to your website for reference."}
            </span>
          </span>
          <button className="cd-refresh-btn" onClick={() => void loadHistory()} disabled={historyLoading || !connected}>
            {historyLoading ? "Scanning…" : "↻ Refresh"}
          </button>
        </div>
        {!connected ? (
          <p className="cd-help">Connect your WordPress site (top of the page) to see the pages this tool has published.</p>
        ) : history.length === 0 ? (
          <p className="cd-help">{historyLoaded ? "No tool-created pages found on this site yet." : "Scanning the site…"}</p>
        ) : (
          <table className="cd-history">
            <thead><tr><th>Title</th><th>URL</th><th>Type</th><th>Uploaded</th></tr></thead>
            <tbody>
              {history.map(p => (
                <tr key={`${p.type}-${p.id}`}>
                  <td>{p.title || "(untitled)"}</td>
                  <td><a href={p.link} target="_blank" rel="noopener noreferrer">{p.link}</a></td>
                  <td style={{ textTransform: "capitalize" }}>{p.type}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtDate(p.created)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
